import { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";

import { Colors, Spacing } from "@paclife/plds.tokens";

import { AppContext } from "context/AppContext";
import Typography from "common/Typography/Typography";
import OverflowMenu from "common/OverflowMenu/OverflowMenu";

import MFAStatus from "components/MFAStatus/MFAStatus";
import MFAModal from "components/MFAModal/MFAModal";
import DeleteModal from "components/DeleteModal/DeleteModal";
import { useTranslation } from "react-i18next";

const Wrapper = styled.div`
    display: flex;
    margin-bottom: ${Spacing[32]};
`;

const Label = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: ${Spacing[8]};
`;

const Text = styled(Typography)`
    display: block;
    color: ${({ color }) => color};
    &.label-text {
        margin-right: ${Spacing[4]};
    }
`;


const ContactNumber = ({ type, idx }) => {
    const { t } = useTranslation("loginSecurity");
    const {
        phoneData
    } = useContext(AppContext);
    const factorType = type.toLowerCase(); 

    const [editModal, setMFAModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [verifyModal, setVerifyModal] = useState(false);
    const [factorId, setFactorId] = useState("");
    const [number, setNumber] = useState("");
    const [status, setStatus] = useState("");

    const openMFAModal = () => {
        setMFAModal(true);
    };

    const openVerifyModal = () => {
        setVerifyModal(true);
    };

    const closeMFAModal = () => {
        setMFAModal(false);
        setVerifyModal(false);
    };

    const openDeleteModal = () => {
        setDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setDeleteModal(false);
    };

    const defaultMenu = [
        {
            icon: "edit",
            color: Colors.neutral1000,
            action: "Update",
            onClick: openMFAModal
        },
        {
            icon: "delete",
            color: Colors.destructive,
            action: "Delete",
            onClick: openDeleteModal
        }
    ];

    const [menu, setMenu] = useState(defaultMenu);

    useEffect(() => {
        clearState();
        if (phoneData.length > 0) {
            if ((phoneData.length === 1 && idx === 0) || phoneData.length === 2) {
                const numberId = phoneData[`${idx}`].number;
                setNumber(numberId);
                setFactorId(Boolean(numberId) ? phoneData[`${idx}`].id : "");
                setStatus(phoneData[`${idx}`].status);
            }
            if (phoneData[`${idx}`]?.status !== "UNVERIFIED") {
                setMenu(defaultMenu);
                return;
            }
            if (menu[0].action !== "Verify") {
                const menuWithVerify = menu.slice();
                menuWithVerify.unshift({
                    icon: "checkmarkOutline",
                    color: Colors.neutral1000,
                    action: "Verify",
                    onClick: openVerifyModal
                });
                setMenu(menuWithVerify);
            }
        }
    }, [phoneData]);

    const clearState = () => {
        setNumber("");
        setFactorId("");
        setStatus("");
    };

    return (
        <div>
            <Wrapper>
                <OverflowMenu
                    icon={Boolean(number) ? "overflowMenuVertical" : "add"}
                    color={Colors.blue600}
                    onClick={openMFAModal}
                    menu={menu}
                />
                <div>
                    <Label>
                        <Text
                            className="label-text"
                            variant="caption"
                            display="block"
                            color={Colors.textBodyLight}
                            bold
                        >
                            {t(`number.${type}`)}
                        </Text>
                    </Label>
                    <Text color={Colors.textBodyLight}>
                        {number || t("number.placeholder")}
                    </Text>
                    <MFAStatus status={status} />
                </div>
            </Wrapper>
           
            {(editModal || verifyModal) && (
                <MFAModal
                    closeModal={closeMFAModal}
                    factorType={factorType}
                    factorId={factorId}
                    defaultValue={number}
                    defaultScreen={verifyModal ? "verify" : "update"}
                />
            )}
            {deleteModal && (
                <DeleteModal
                    closeModal={closeDeleteModal}
                    factorType={type}
                    defaultValue={number}
                    subType="phone"
                    factorId={factorId}
                />
            )}
        </div>
    );
};

export default ContactNumber;

ContactNumber.propTypes = {
    type: PropTypes.oneOf(["sms", "call"])
};
